/**
 * One Suspense boundary per Work component port, applied when the bundle is stored.
 * A class component is the slot witness: hooks and render helpers are not views.
 */
import {
  forwardRef,
  Suspense,
  type ComponentClass,
  type ComponentType,
  type ReactNode,
} from 'react';

import type { WorkHostBundleName, WorkHostBundles } from './work-home-ports';
import {
  CenteredSpinner,
  ChunkErrorText,
  LiveViewFrame,
  PatientPanelSkeleton,
  PreviewSkeletonBody,
  RoutineOverviewSkeleton,
  TemplateBuilderResolvingSkeleton,
} from './work-host-view-fallbacks';

type IsViewSlot<T> = unknown extends T
  ? false
  : // Witness only: a class component is assignable to a view, and its props are unused.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [ComponentClass<any>] extends [NonNullable<T>]
    ? true
    : false;

type ViewSlotOf<B> = {
  [K in keyof B]-?: IsViewSlot<B[K]> extends true ? K : never;
}[keyof B];

type SlotProps<T> = T extends ComponentType<infer P> ? P : never;

export type WorkHostViewBound<P> =
  | { kind: 'bounded'; fallback: null | ((props: P) => ReactNode) }
  | { kind: 'consumer'; sites: readonly string[] };

export type WorkHostViewBounds = {
  [N in WorkHostBundleName]: {
    [K in ViewSlotOf<WorkHostBundles[N]>]: WorkHostViewBound<
      SlotProps<NonNullable<WorkHostBundles[N][K]>>
    >;
  };
};

const bounded = { kind: 'bounded', fallback: null } as const;

export const WORK_HOST_VIEW_BOUNDS = {
  desktop: {
    DesktopBars: bounded,
    ParamChip: bounded,
    TemplateParts: bounded,
    CloudComputerExpandButton: bounded,
    CloudComputerLightbox: bounded,
    CloudComputerLiveView: {
      kind: 'bounded',
      fallback: (props) => <LiveViewFrame className={props.className} />,
    },
    OverlayShimmerText: bounded,
    CloudComputerInlineLiveView: bounded,
    DesktopControlApprovalWarning: bounded,
    ProtectedActionApproval: bounded,
  },
  session: {
    EmbeddingsPanel: {
      kind: 'bounded',
      fallback: () => <PreviewSkeletonBody />,
    },
    RecorderSessionScope: bounded,
    RegulatoryGuidelineBlockModal: bounded,
    RegulatoryGuidelineWarningModal: bounded,
    PatientDocumentsSection: {
      kind: 'bounded',
      fallback: () => <PatientPanelSkeleton />,
    },
    DrawerSections: {
      kind: 'bounded',
      fallback: () => <PatientPanelSkeleton />,
    },
    SessionHistorySection: {
      kind: 'bounded',
      fallback: () => <PatientPanelSkeleton />,
    },
    SlideEditorPanel: {
      kind: 'bounded',
      fallback: () => <CenteredSpinner size={8} />,
    },
    SlideUndoRedoButtons: bounded,
    SessionArtifactModalHosts: bounded,
    LinkedSessionTabsHost: {
      kind: 'bounded',
      fallback: () => <PreviewSkeletonBody />,
    },
    SplitScreenSkeletonLoader: bounded,
    ClinicalTasksPanelHostProvider: {
      kind: 'bounded',
      fallback: () => <PreviewSkeletonBody />,
    },
    TemplateMarkdownPreview: {
      kind: 'bounded',
      fallback: () => <TemplateBuilderResolvingSkeleton />,
    },
    DraftNoteBody: {
      kind: 'bounded',
      fallback: () => <PreviewSkeletonBody />,
    },
  },
  evidence: {
    LinkedContextBadge: bounded,
    SearchGroupTransition: bounded,
    ChatObjectReference: bounded,
    RefMarkdownElement: bounded,
    ImageFileCard: bounded,
    ComposerShortcutMarketplace: bounded,
    ChatObjectTokenHoverCard: bounded,
    EvidenceAnswerPortsHost: bounded,
    EvidenceRegulatoryFootnoteWithIfu: bounded,
    InSessionSourcesButton: bounded,
    ChatObjectList: bounded,
  },
  integrations: {
    MergeConnectButton: bounded,
    NangoConnectButton: bounded,
  },
  routines: {
    VoiceWindow: bounded,
    RoutineOverviewPane: {
      kind: 'bounded',
      fallback: () => <RoutineOverviewSkeleton />,
    },
    RoutineRecentRunsRail: bounded,
    RoutineDockCarousel: bounded,
    WorkflowsSplitView: {
      kind: 'bounded',
      fallback: () => <CenteredSpinner size={5} />,
    },
    VoiceAssistantTrigger: bounded,
    RoutineRunChatHeader: bounded,
    CreateWorkflowApprovalCard: bounded,
    RoutineCreateGuidedChat: bounded,
    RecordingWorkflowSummary: bounded,
    WorkflowEditorBoldButton: bounded,
    WorkflowEditorItalicButton: bounded,
    WorkflowEditorBulletListButton: bounded,
  },
  shell: {
    ChunkErrorMessage: {
      kind: 'bounded',
      fallback: (props) => <ChunkErrorText>{props.children}</ChunkErrorText>,
    },
    OpenTemplateEditorBlock: bounded,
    TemplatePinBlock: bounded,
    WorkArtefactCreatedCard: bounded,
    ComposerDictateSlot: bounded,
    ExperienceFeedbackNudge: bounded,
  },
  bridge: {
    EvidenceTimelineBody: bounded,
    WorkSourcesPopover: {
      kind: 'consumer',
      sites: ['LinkContextButton.tsx', 'LinkContextButton.parts.tsx'],
    },
  },
} satisfies WorkHostViewBounds;

const boundBrand: unique symbol = Symbol('work-host-bound-view');

/** Own descriptors. A getter-only slot is rebound below and stays unread until used. */
const copyOwn = <T extends object>(bundle: T): T =>
  Object.defineProperties({}, Object.getOwnPropertyDescriptors(bundle)) as T;

const isBoundView = (value: unknown): boolean =>
  (typeof value === 'object' || typeof value === 'function') &&
  value !== null &&
  boundBrand in value;

const wrapView = (
  bundleName: string,
  slot: string,
  Inner: ComponentType<Record<string, unknown>>,
  fallback: ((props: Record<string, unknown>) => ReactNode) | null
) => {
  const view = forwardRef<unknown, Record<string, unknown>>((props, ref) => (
    <Suspense fallback={fallback ? fallback(props) : null}>
      <Inner {...props} ref={ref} />
    </Suspense>
  ));
  view.displayName = `WorkHostView(${bundleName}.${slot})`;
  Object.assign(view, { [boundBrand]: true });
  return view;
};

/** Copy-on-write. Nothing to bind returns the same object; an absent member stays absent. */
export const bindWorkHostViews = <N extends WorkHostBundleName>(
  name: N,
  bundle: WorkHostBundles[N] | undefined
): WorkHostBundles[N] | undefined => {
  if (bundle === undefined) return bundle;
  const table = WORK_HOST_VIEW_BOUNDS[name] as unknown as Record<
    string,
    WorkHostViewBound<Record<string, unknown>>
  >;
  let copy: WorkHostBundles[N] | undefined;
  for (const slot of Object.keys(table)) {
    const row = table[slot];
    if (!row || row.kind !== 'bounded') continue;
    const fallback = row.fallback as
      | ((props: Record<string, unknown>) => ReactNode)
      | null;
    const desc = Object.getOwnPropertyDescriptor(bundle, slot);
    if (desc?.get) {
      const views = new WeakMap<object, unknown>();
      copy ??= copyOwn(bundle);
      Object.defineProperty(copy, slot, {
        configurable: true,
        enumerable: desc.enumerable,
        get() {
          const inner = desc.get?.call(bundle) as object | null | undefined;
          if (inner == null || isBoundView(inner)) return inner;
          let view = views.get(inner);
          if (!view) {
            view = wrapView(
              name,
              slot,
              inner as ComponentType<Record<string, unknown>>,
              fallback
            );
            views.set(inner, view);
          }
          return view;
        },
      });
      continue;
    }
    const member = (bundle as unknown as Record<string, unknown>)[slot];
    if (member == null || isBoundView(member)) continue;
    copy ??= copyOwn(bundle);
    (copy as unknown as Record<string, unknown>)[slot] = wrapView(
      name,
      slot,
      member as ComponentType<Record<string, unknown>>,
      fallback
    );
  }
  return copy ?? bundle;
};
